/**
 * ReassignLogic Component
 * ---------------------------------------------------------
 * This component allows the admin/team lead to create rules
 * for automatically reassigning *unattended leads* after a 
 * configured time interval (Rotation Logic).
 *
 * Features:
 *  - Select Lead Source
 *  - Select Project
 *  - Select Rotation Time (minutes)
 *  - Select Lead Manager(s)
 *  - Select Associate(s)
 *  - Duplicate-rule validation (Source + Project)
 *  - Generate unique rotation rule (UUID)
 *  - Save rule to Firestore
 */

import { FunctionComponent, useState } from "react";
import { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import styles from "./LeadDistributorLogic.module.css";
import { themeColors } from "../../Components/theme";
import Dropdown from "./Dropdown";
import { contactResources, editReassignLogic, fetchConstants, fetchpropertySubType, fetchRotationData, fetchRotationTimeData } from "../../Services/contacts";
import { showSnackbarAction } from "../../Redux/actions";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Components/Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import Firebase from "firebase/app";

interface LocationState {
  data: any; // Type this according to your actual data structure
  index: number;
}

type props = {
  history?: any;
  user?: any;
  organizationUsers?: any;
  theme?: any;
  conatctHeader?: any;
};

const EditReassignLogic: FunctionComponent<props> = ({
  history,
  user,
  organizationUsers,
  theme,
  conatctHeader,
}) => {

  /**
 * ---------------------------------------------------------
 * UI & Component State
 * ---------------------------------------------------------
 */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  // Scroll container height
  const [maxHeight, setMaxHeight] = useState(window.innerHeight * 0.8);

  // Accordion open/close
  const [expandedId, setExpandedId] = useState<string | null>("1");

  // Force re-render on updates
  const dispatcher = useDispatch();

  // Router data (if editing)
  const location = useLocation<LocationState>();

  /**
 * Selected dropdown values
 */
  const [projectSelected, setProjectSelected] = useState<any>([]);

  // Active rotation rule list for duplicate validation
  const [filterData, setFilterData] = useState<any[]>([]);

  const [sourceSelected, setSourceSelected] = useState<any>();
  const [sourceDuplicateSelected, setDuplicateSourceSelected] = useState<boolean>(false);
  const [associateSelected, setAssociateSelected] = useState<any>([]);
  const [leadManagerSelected, setLeadManagerSelected] = useState<any>([]);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [locationsList, setLocationsList] = useState<any[]>([]);
  const [budgetsList, setBudgetsList] = useState<any[]>([]);
  const [propertyTypeList, setPropertyTypeList] = useState<any[]>([]);
  const [propertyStageList, setPropertyStageList] = useState<any[]>([]);
  const [notIntReasonList, setNotIntReasonList] = useState<any[]>([]);
  const [lostReasonList, setLostReasonList] = useState<any[]>([]);
  const [load, setLoad] = useState(false);
  const [leadSourceList, setLeadSourceList] = useState<any[]>([]);
  const [comTypes, setComTypes] = useState<any[]>([]);
  const [resTypes, setResTypes] = useState<any[]>([]);
  const [associateList, setAssociateList] = useState<any[]>([]);
  const [rotationTime, setRotationTime] = useState<any[]>([]);
  const [rotationTimeSelected, setRotationTimeSelected] = useState<any>({});
  const [associateRotationList, setAssociateRotationList] = useState<any[]>([]);
  const [leadManagerList, setLeadManagerList] = useState<any[]>([]);
  const [rotationLeadDistId, setRotationLeadDistId] = useState<string>("");
  const [rotationIndex, setRotationIndex] = useState<number>(0);

  // UID map for each user
  const [uid, setUids] = useState<any>({});
  const { data, index } = location.state || {};

  const handleResize = () => {
    setMaxHeight(window.innerHeight * 0.8);
  };

  /**
   * ---------------------------------------------------------
   * Window Resize Handler (adjust scrollable area)
   * ---------------------------------------------------------
   */
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
 * ---------------------------------------------------------
 * Fetch existing rotation rules (for duplicate validation)
 * ---------------------------------------------------------
 */
  useEffect(() => {
    if (user.organization_id) {
      const unsub = fetchRotationData(user.organization_id, (data) =>
        setFilterData(data)
      );
      return () => {
        unsub();
      };
    }
  }, [user]);

  /**
 * ---------------------------------------------------------
 * Fetch Rotation Time options (like 5 mins, 10 mins, etc.)
 * ---------------------------------------------------------
 */
  useEffect(() => {
    if (user.organization_id) {
      const unsub = fetchRotationTimeData(
        (data) => setRotationTime(data)
      );
      return () => {
        unsub();
      };
    }
  }, [user]);

  /**
 * ---------------------------------------------------------
 * Build Lead Manager dropdown
 * ---------------------------------------------------------
 */
  useEffect(() => {
    let leadManagerData: any = [];
    organizationUsers?.forEach((item: any) => {
      const fullNameEmail = `${item.user_first_name} ${item.user_last_name} - ${item.user_email}`;
      if (item.profile === "Lead Manager") {
        leadManagerData.push(fullNameEmail);
      }
    });
    setLeadManagerList(leadManagerData);
  }, [organizationUsers]);

  /**
 * Associate List Auto-Fill based on Lead Manager selection
 */
  useEffect(() => {
    if (leadManagerSelected.length > 0) {
      let associateData: any = [];
      leadManagerSelected.forEach((item: any) => {
        if (item?.value !== "Select") {
          let email: any;
          if (item?.value.includes("-")) {
            let l = item?.value?.split("-");
            if (l && l.length > 1) {
              email = l[1].trim();
            }
          } else {
            email = item?.value.trim();
          }
          organizationUsers?.forEach((orgUser: any) => {
            const fullNameEmail = `${orgUser.user_first_name} ${orgUser.user_last_name} - ${orgUser.user_email}`;
            if (orgUser.reporting_to?.toLowerCase() === email.toLowerCase()) {
              associateData.push(fullNameEmail);
            }
          });
        }
      });
      setAssociateRotationList(associateData);
    }
  }, [leadManagerSelected, organizationUsers]);

  /**
 * ---------------------------------------------------------
 * Build UID lookup & Associate list
 * ---------------------------------------------------------
 */
  useEffect(() => {
    let data: any = [];
    let uids: { [key: string]: string } = {};
    organizationUsers?.forEach((item: any, index: number) => {
      data.push(
        `${item.user_first_name} ${item.user_last_name} -${item.user_email}`
      );
      uids[item.user_email] = item.uid;
    });

    setUids(uids);
    setAssociateList(data);
  }, [organizationUsers]);

  /**
 * ---------------------------------------------------------
 * Duplicate Rule Validation (same Source + same Project)
 * ---------------------------------------------------------
 */
  useEffect(() => {
    if (
      sourceSelected &&
      typeof sourceSelected === "object" &&
      sourceSelected.value &&
      projectSelected &&
      Array.isArray(projectSelected) &&
      projectSelected.length > 0
    ) {
      const isSourceAndProjectPresent = filterData.some((item) => {
        const isSourceMatch = item.source === sourceSelected.value;
        const isProjectMatch = projectSelected.some(
          (project) => item.project.includes(project.value)
        );
        return isSourceMatch && isProjectMatch;
      });

      if (isSourceAndProjectPresent) {
        setDuplicateSourceSelected(true);
        dispatcher(
          showSnackbarAction(
            `Lead distribution logic is already created for ${sourceSelected.value} and selected projects`,
            "error"
          )
        );
      } else {
        setDuplicateSourceSelected(false);
        console.log("Selected value does not exist in filterData for both source and project.");
      }
    }
    else if (
      sourceSelected &&
      typeof sourceSelected === "object" &&
      sourceSelected.value
    ) {
      const isSourcePresent = filterData.some(
        (item) => item.source === sourceSelected.value
      );
      if (isSourcePresent) {
        setDuplicateSourceSelected(true);
        dispatcher(
          showSnackbarAction(
            `Lead distribution logic is already created on ${sourceSelected.value}`,
            "error"
          )
        );
      } else {
        setDuplicateSourceSelected(false);
        console.log("Selected value does not exist in filterData");
      }
    }

  }, [sourceSelected, projectSelected]);

  // ---------------------------------------------------------
  // ON PAGE LOAD → Pre-fill form with existing distribution data
  // ---------------------------------------------------------
  useEffect(() => {
    if (data) {
      let project = data.project.map((item: any, index: number) => {
        return { value: item, label: item };
      });

      let leadManager = data?.leadManager_users?.map(
        (item: any, index: number) =>
          item === "Team Lead"
            ? {
              value: item?.user_email,
              label: "Team Leader",
            }
            : { value: item?.user_email, label: item?.user_email }
      );

      setRotationTimeSelected({
        value: data.rotation_time,
        label: `${data.rotation_time} mins`
      });
      setProjectSelected(project);
      setSourceSelected({ value: data.source, label: data.source });
      setLeadManagerSelected(leadManager);
      setRotationLeadDistId(data?.reloc_id);
      setRotationIndex(data?.userIndex);
    }
  }, []);

  useEffect(() => {
    if (associateRotationList.length > 0) {
      const labelMap = Object.fromEntries(
        associateRotationList.map((entry) => {
          const [name, email] = entry.split(" - ");
          return [email, entry];
        })
      );
      const updatedUserList = data.users.map((email: any) => ({
        value: labelMap[email] || email,
        label: labelMap[email] || email,
      }));
      setAssociateSelected(updatedUserList);
    }
  }, [associateRotationList, data, leadManagerSelected]);

  /**
  * ---------------------------------------------------------
  * Fetch all CRM resources (Projects, Budgets, Locations, Sources)
  * ---------------------------------------------------------
  */
  useEffect(() => {
    const unsub = contactResources(
      user.organization_id,
      (data) => setLocationsList(data),
      (data) => setBudgetsList(data),
      (data) => setProjectsList(data)
    );
    const unsubConst = fetchConstants(
      (data) => setNotIntReasonList(data),
      (data) => setLostReasonList(data),
      (data) => setPropertyStageList(data),
      (data) => setPropertyTypeList(data)
    );
    const unsubProperty = fetchpropertySubType(
      user.organization_id,
      (data) => setComTypes(data),
      (data) => setResTypes(data),
      (data) => setLeadSourceList(data)
    );
    return () => {
      unsub();
      unsubConst();
      unsubProperty();
    };
  }, []);

  /**
 * Accordion Open/Close Handler
 */
  const toggleAccordion = (id: string) => {
    if (id === expandedId) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
    }
  };

  /**
   * ---------------------------------------------------------
   * onSubmit → Final save logic
   * ---------------------------------------------------------
   * Validates:
   *  - Lead Manager
   *  - Associate
   *  - Lead Source
   *  - Rotation Time
   *
   * Generates:
   *  - Unique Rule ID (UUID)
   *  - Users Queue for rotation cycles
   *
   * Sends rule to Firestore
   */
  const onSubmit = () => {
    let source: any = [];
    let project: any = [];
    let usersList: any = [];
    let usersQueue: any = [];
    let LeadManagerList: any = [];

    // Projects
    projectSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        project.push(item.value);
      }
    });

    // Source
    if (
      sourceSelected &&
      typeof sourceSelected === "object" &&
      sourceSelected.value !== "Select"
    ) {
      source.push(sourceSelected.value);
    }

    // Associates
     if (Array.isArray(leadManagerSelected)) {
      leadManagerSelected.forEach((item: any) => {
        if (item.value !== "Select") {
          let l = item.value?.split("-"); // Split based on "-"
          let email = l && l.length > 1 ? l[1].trim() : item.value; // Get the email or use the value directly if no dash exists

          if (uid[email.trim()] !== undefined) {
            LeadManagerList.push({
              uid: uid[email.trim()],
              user_email: email.trim(),
            });
          }
        }
      });
    } else {
      if (leadManagerSelected?.value !== "Select") {
        let l = leadManagerSelected.value?.split("-"); // Split based on "-"
        let email = l && l.length > 1 ? l[1].trim() : leadManagerSelected.value; // Get the email or use the value directly if no dash exists

        if (uid[email.trim()] !== undefined) {
          LeadManagerList.push({
            uid: uid[email.trim()],
            user_email: email.trim(),
          });
        }
      }
    }

     if (Array.isArray(associateSelected)) {
      associateSelected.forEach((item: any) => {
        if (item.value !== "Select") {
          let l = item.value?.split("-");
          let email = l && l.length > 1 ? l[1].trim() : item.value; // Extract email or use value directly if no "-"

          if (uid[email.trim()] !== undefined) {
            usersList.push({
              uid: uid[email.trim()],
              user_email: email.trim(),
            });
            usersQueue.push(email);
          }
        }
      });
    } else {
      if (associateSelected?.value !== "Select") {
        let l = associateSelected.value?.split("-");
        let email = l && l.length > 1 ? l[1].trim() : associateSelected.value; // Extract email or use value directly if no "-"

        if (uid[email.trim()] !== undefined) {
          usersList.push({
            uid: uid[email.trim()],
            user_email: email.trim(),
          });
          usersQueue.push(email);
        }
      }
    }

    // ---- Validations ----
    if (LeadManagerList.length === 0) {
      dispatcher(showSnackbarAction("Please select a Lead Manager", "error"));
    } else if (usersList.length === 0) {
      dispatcher(showSnackbarAction("Please select an Associate", "error"));
    } else if (
      source.length === 0
    ) {
      dispatcher(showSnackbarAction("Please select a Lead Source", "error"));
    }
    else if (!rotationTimeSelected || (Object.keys(rotationTimeSelected).length === 0 && rotationTimeSelected.constructor === Object) || rotationTimeSelected.value === ""
    ) {
      dispatcher(showSnackbarAction("Please select Rotation Time", "error"));
    }
    else {
      setLoad(true);
      const normalizeUsersData = (users: any) => {
        return users.map((user: any) => {
          const userEmail = typeof user === "string" ? user : user.user_email;
          return {
            user_email: userEmail,
            uid: uid[userEmail] || "", // Look up UID from the mapping
          };
        });
      };
      const normalizeLeadManagerData = (leadManagerUsers: any) => {
        return leadManagerUsers.map((user: any) => {
          const userEmail = typeof user === "string" ? user : user.user_email;
          return {
            user_email: userEmail,
            uid: uid[userEmail] || "", // Look up UID from the mapping
          };
        });
      };
      if (index !== undefined) {
        filterData[index] = {
          source: source[0],
          project: [].concat(...project),
          users: normalizeUsersData(usersList),
          leadManager_users: normalizeLeadManagerData(LeadManagerList),
          usersQueue: [].concat(...usersQueue),
          created_at: Firebase.firestore.Timestamp.now(),
          modify_at: Firebase.firestore.Timestamp.now(),
          userIndex: rotationIndex,
          reloc_id: rotationLeadDistId,
          rotation_time:rotationTimeSelected.value
        };
      
        editReassignLogic(
          uid,
          user.organization_id,
          filterData,
          dispatcher,
          (data) => setLoad(data),
          history
        );
      }
    }
  };

  /**
    * Theme-based styling
    */
  const containerStyles = {
    backgroundColor: theme
      ? themeColors.backgroundColor
      : themeColors.backgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  };

  const cardContainerStyles = {
    backgroundColor: theme
      ? themeColors.cardBackgroundColor
      : themeColors.cardBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  };

  /**
   * ---------------------------------------------------------
   * JSX UI Rendering
   * ---------------------------------------------------------
   */
  return (
    <div className={styles.parent} style={{ display: "block", marginTop: isMobile ? "6%" : "3%", ...containerStyles }}>
      <div className={`container-fluid ${isMobile ? "p-2" : "p-4"} d-flex flex-column gap-4`}>
        {/* Mobile header */}
        {isMobile && (
          <div className="d-flex justify-content-between">
            <h5 style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-large)", paddingLeft: "5px", paddingTop: "5px", margin: 0 }}>Reassign Logic Section</h5>
          </div>
        )}
        {/* Scrollable form */}
        <div
          className="d-flex flex-column gap-4"
          style={{
            ...containerStyles,
            maxHeight: `${maxHeight}px`,
            overflowX: "hidden",
            overflowY: "scroll",
            paddingBottom: "5%",
          }}
        >
          {/* Accordion Card */}
          <div className={`card ${isMobile ? "p-2" : "p-4"} shadow rounded`} style={cardContainerStyles}>
              <>
                <form
                  className={`card shadow rounded mt-2${!isMobile ? ` ${styles.forcePadding}` : ""}`}
                  style={{
                    ...cardContainerStyles,
                    padding: isMobile ? '16px' : '32px',
                    boxSizing: 'border-box',
                    minHeight: isMobile ? 'auto' : undefined,
                  }}
                >
                  <Grid container spacing={isMobile ? 2 : 3}>
                    <Grid item xs={12} sm={4}>
                      <Dropdown
                        title={
                          conatctHeader.find(
                            (obj: any) => obj.label === "lead_source"
                          ).value
                        }
                        option={leadSourceList}
                        selectedValue={sourceSelected}
                        setSelectedValue={(value) => {
                          setSourceSelected(value);
                        }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Dropdown
                        title={
                          conatctHeader.find(
                            (obj: any) => obj.label === "project"
                          ).value
                        }
                        isMulti
                        option={projectsList}
                        selectedValue={projectSelected}
                        setSelectedValue={(value) => {
                          setProjectSelected(value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Dropdown
                        title="Rotation Time(in mins)"
                        option={rotationTime}
                        selectedValue={rotationTimeSelected}
                        setSelectedValue={(value) => {
                          setRotationTimeSelected(value);
                        }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Dropdown
                        title="Lead Manager"
                        isMulti
                        option={leadManagerList}
                        selectedValue={leadManagerSelected}
                        setSelectedValue={(value) => {
                          setLeadManagerSelected(value);
                        }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Dropdown
                        title="Associate"
                        isMulti
                        option={associateRotationList}
                        selectedValue={associateSelected}
                        setSelectedValue={(value) => {
                          setAssociateSelected(value);
                        }}
                        required
                      />
                    </Grid>
                    {/* Remove submit button from inside form for mobile */}
                    <Grid item xs={12} style={{ marginTop: 12 }}>
                      <StyledSubmitButton
                        disabled={sourceDuplicateSelected}
                        onClick={onSubmit}
                        themeMode={themeMUI.palette.mode}
                      >
                        {load ? (
                          <span style={{ display: "flex", justifyContent: "center" }}>
                            <Circles color="#fff" height={20} width={20} />
                          </span>
                        ) : (
                          <>Apply</>
                        )}
                      </StyledSubmitButton>
                    </Grid>
                  </Grid>
                </form>
              </>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Map Redux state to component props
 */
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    organizationUsers: state.organizationUsers.data,
    theme: state.theme.isLightMode,
    conatctHeader: state.contactHeaderList.data,
  };
};

export default connect(mapStateToProps)(EditReassignLogic);
